/**
 * Reading a bot answer back out of Chatwoot when the SSE stream never delivered
 * it.
 *
 * The widget renders Ella's answer from the `delta` stream, but glue writes that
 * same answer into Chatwoot independently, and it does so *before* it is done
 * with the stream (see `chatwoot_echo` in customer-support
 * `glue/src/chat-route.ts`). So when the browser's half of the stream is cut —
 * the visitor closed the panel, navigated away, lost the network — the answer
 * still exists; it just never arrived. Chatwoot holds the authoritative
 * transcript, so we ask it instead of telling the visitor something went wrong
 * (AHD-9158).
 *
 * Deliberately carries no dedupe of its own: callers filter candidates through
 * `isBotEcho`, which already owns the question "has the widget shown this?".
 */

export interface ChatwootApiMessage {
  id: number
  content?: string
  message_type: number
  private?: boolean
  /** Epoch seconds, as Chatwoot's admin API reports it. */
  created_at?: number
  sender?: { name?: string; avatar_url?: string }
}

export interface OutgoingMessage {
  id: number
  content: string
  senderName?: string
  /** The agent's Chatwoot profile photo, when they've set one. */
  senderAvatarUrl?: string
  /** ISO timestamp, or null when Chatwoot didn't give us one. */
  createdAt: string | null
}

/**
 * Outgoing (bot or agent) messages that have something to render, oldest first.
 * `afterId` bounds the result to messages newer than the transcript's high-water
 * mark; pass null to leave it unbounded.
 */
export function selectOutgoingMessages(
  messages: readonly ChatwootApiMessage[],
  options: { afterId: number | null }
): OutgoingMessage[] {
  const { afterId } = options
  return messages
    .filter((m) => m.message_type === 1 && !m.private)
    .filter((m) => afterId === null || m.id > afterId)
    .flatMap((m) => {
      const content = (m.content ?? "").trim()
      if (!content) return []
      return [
        {
          id: m.id,
          content,
          senderName: m.sender?.name,
          senderAvatarUrl: m.sender?.avatar_url,
          createdAt:
            typeof m.created_at === "number"
              ? new Date(m.created_at * 1000).toISOString()
              : null,
        },
      ]
    })
    .sort((a, b) => a.id - b.id)
}

/**
 * Fetches the conversation's messages through the site's own proxy.
 *
 * Throws on anything that isn't a well-formed response: a heal that quietly
 * returns nothing is indistinguishable from "Chatwoot genuinely had nothing",
 * and those two need different handling — one is a bug worth reporting, the
 * other is the normal end of the road.
 */
export async function fetchConversationMessages(args: {
  conversationId: number
  sourceId: string
  afterId: number | null
}): Promise<ChatwootApiMessage[]> {
  const url = new URL("/api/chat/messages", window.location.origin)
  url.searchParams.set("conversation_id", String(args.conversationId))
  if (args.afterId !== null) {
    url.searchParams.set("after", String(args.afterId))
  }

  const res = await fetch(url.toString(), {
    headers: { "X-Auth-Token": args.sourceId },
  })
  if (!res.ok) {
    throw new Error(`/api/chat/messages returned ${res.status}`)
  }

  const data = (await res.json()) as { messages?: unknown }
  if (!Array.isArray(data.messages)) {
    throw new Error("/api/chat/messages returned no messages array")
  }
  return data.messages as ChatwootApiMessage[]
}
